/**
 * DENEB London Theme - Header
 *
 * Extracted from sections/header.liquid (Task 20d) so the header JS can be
 * cached as a static asset instead of re-sent inline on every page view.
 *
 * The original script carried no Liquid interpolations; an empty
 * <script type="application/json" data-header-config> element is still
 * rendered by the section to serve as the editor re-init gate (mirroring the
 * data-collection-config / #cart-drawer pattern used elsewhere).
 *
 * Globals relied upon (defined elsewhere, intentionally not duplicated):
 *   - openCartDrawer() -> assets/cart-drawer.js (referenced via onclick in markup)
 *
 * Functions this file exposes on window (referenced elsewhere in the theme):
 *   - updateHeaderWishlistCount() -> wishlist count badge refresh
 *
 * Editor-safe init: initialises once on normal page load, and re-initialises
 * against fresh DOM on shopify:section:load when the header section reloads.
 * Per-section listeners (hamburger, accordions, search input, announcement
 * close) re-bind against the fresh DOM each init. Document/window-level
 * listeners (search click-outside, storage, scroll handlers) bind only once
 * (guarded by documentListenersBound) and read live DOM at event time. The
 * announcement rotation setInterval id is held at module scope and cleared
 * before starting a new one so editor reloads don't leak stacked intervals.
 */
(function () {
  var documentListenersBound = false;
  var announcementIntervalId = null;
  var mainContentObserver = null;
  // Module-scope reference updated by each initHeader() call so the
  // once-bound document keydown handler can reach the current closeMenu().
  var activeCloseMenu = null;
  // AbortController for the in-flight predictive search request; replaced on
  // each new fetch so rapid typing cancels the previous request.
  var searchAbort = null;

  // Update wishlist count badge. Exposed on window so other scripts can call
  // it after wishlist mutations. Defined at module scope so the once-bound
  // document/window listeners can reference it across re-inits.
  function updateWishlistCount() {
    try {
      var wishlist = JSON.parse(localStorage.getItem('deneb_wishlist') || '[]');
      var count = wishlist.length;
      var badges = document.querySelectorAll('[data-wishlist-count]');

      badges.forEach(function (badge) {
        badge.textContent = count > 0 ? count : '';
      });
    } catch (e) {
      console.error('Error updating wishlist count:', e);
    }
  }

  function readHeaderConfig() {
    var configEl = document.querySelector('[data-header-config]');
    if (!configEl) return {};
    try {
      return JSON.parse(configEl.textContent) || {};
    } catch (e) {
      return {};
    }
  }

  function updateHeroHeaderState() {
    var header = document.querySelector('.header');
    var shell = document.querySelector('.site-header-shell');
    if (!header) return;

    var main = document.getElementById('main-content');
    var firstSection = main && main.firstElementChild;
    var hero = firstSection && firstSection.querySelector('.ai-video-hero');
    var shouldOverlay = !!hero;
    var overlayChanged = header.classList.contains('header--hero-overlay') !== shouldOverlay;

    header.classList.toggle('header--hero-overlay', shouldOverlay);
    header.classList.toggle('header--solid', shouldOverlay && window.pageYOffset > 24);
    if (shell) shell.classList.toggle('site-header-shell--hero', shouldOverlay);

    // Avenith swaps only <main>. If a swap adds the homepage hero, remeasure it
    // after the fixed overlay header has left document flow so it stays 100vh.
    if (overlayChanged && shouldOverlay && typeof hero.updateHeaderOffset === 'function') {
      window.requestAnimationFrame(function () {
        hero.updateHeaderOffset();
      });
    }
  }

  function observeMainContent() {
    if (mainContentObserver || !window.MutationObserver) return;
    var main = document.getElementById('main-content');
    if (!main) return;
    mainContentObserver = new MutationObserver(updateHeroHeaderState);
    mainContentObserver.observe(main, { childList: true });
  }

  function initHeader() {
    var configEl = document.querySelector('[data-header-config]');
    if (!configEl) return;

    var headerConfig = readHeaderConfig();
    var searchUrl = headerConfig.searchUrl || '/search';
    var predictiveSearchUrl = headerConfig.predictiveSearchUrl || '/search/suggest.json';

    // Guard against double-init against the same DOM.
    if (document.body && document.body.dataset.headerJsInit === 'true') return;
    if (document.body) document.body.dataset.headerJsInit = 'true';

    var hamburger = document.querySelector('.header__hamburger');
    var slideMenu = document.querySelector('.header__slide-menu');
    var closeBtn = document.querySelector('.header__slide-menu-close');
    var overlay = document.querySelector('.header__overlay');

    updateHeroHeaderState();
    observeMainContent();

    function openMenu() {
      slideMenu.classList.add('header__slide-menu--open');
      overlay.classList.add('header__overlay--visible');
      document.body.style.overflow = 'hidden';
      if (hamburger) hamburger.setAttribute('aria-expanded', 'true');
      // Move focus to the close button so keyboard/AT users are inside the menu
      if (closeBtn) closeBtn.focus();
    }

    function closeMenu() {
      var wasOpen = slideMenu.classList.contains('header__slide-menu--open');
      slideMenu.classList.remove('header__slide-menu--open');
      overlay.classList.remove('header__overlay--visible');
      document.body.style.overflow = '';
      if (hamburger) hamburger.setAttribute('aria-expanded', 'false');
      // Close all accordions
      document.querySelectorAll('.header__slide-accordion--open').forEach(function (accordion) {
        accordion.classList.remove('header__slide-accordion--open');
        accordion.querySelector('.header__slide-accordion-toggle').setAttribute('aria-expanded', 'false');
      });
      // Return focus to the hamburger so keyboard users don't lose their place.
      // Only focus if the menu was actually open (avoids spurious focus jumps on
      // programmatic calls when the menu is already closed).
      if (wasOpen && hamburger) hamburger.focus();
    }

    // Expose current closeMenu so the once-bound document keydown handler
    // (which cannot be rebound on editor re-init) can reach it.
    activeCloseMenu = closeMenu;

    if (hamburger) hamburger.addEventListener('click', openMenu);
    if (closeBtn) closeBtn.addEventListener('click', closeMenu);
    if (overlay) overlay.addEventListener('click', closeMenu);

    // Progressive enhancement: cart icon links fall back to /cart without JS;
    // with JS, intercept and open the drawer instead.
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach(function (el) {
      el.addEventListener('click', function (e) {
        if (typeof window.openCartDrawer === 'function') {
          e.preventDefault();
          window.openCartDrawer();
        }
      });
    });

    // Mobile Menu Accordions
    var accordionToggles = document.querySelectorAll('.header__slide-accordion-toggle');

    accordionToggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var accordion = this.closest('.header__slide-accordion');
        var isOpen = accordion.classList.contains('header__slide-accordion--open');

        // Close all other accordions
        document.querySelectorAll('.header__slide-accordion--open').forEach(function (openAccordion) {
          if (openAccordion !== accordion) {
            openAccordion.classList.remove('header__slide-accordion--open');
            openAccordion.querySelector('.header__slide-accordion-toggle').setAttribute('aria-expanded', 'false');
          }
        });

        // Toggle current accordion
        if (isOpen) {
          accordion.classList.remove('header__slide-accordion--open');
          this.setAttribute('aria-expanded', 'false');
        } else {
          accordion.classList.add('header__slide-accordion--open');
          this.setAttribute('aria-expanded', 'true');
        }
      });
    });

    // Initial wishlist count update
    updateWishlistCount();

    // Make update function globally available
    window.updateHeaderWishlistCount = updateWishlistCount;

    // Predictive Search
    var searchInput = document.querySelector('[data-search-input]');
    var searchResults = document.querySelector('[data-search-results]');
    var predictiveSearch = document.getElementById('predictive-search-results');
    var searchTimeout;

    if (searchInput && searchResults) {
      searchInput.addEventListener('input', function () {
        var query = this.value.trim();

        clearTimeout(searchTimeout);

        if (query.length < 2) {
          if (searchAbort) {
            searchAbort.abort();
            searchAbort = null;
          }
          while (searchResults.firstChild) {
            searchResults.removeChild(searchResults.firstChild);
          }
          predictiveSearch.classList.remove('is-visible');
          this.setAttribute('aria-expanded', 'false');
          return;
        }

        searchTimeout = setTimeout(function () {
          predictiveSearch.setAttribute('aria-busy', 'true');

          if (searchAbort) searchAbort.abort();
          searchAbort = new AbortController();

          fetch(predictiveSearchUrl + '?q=' + encodeURIComponent(query) + '&resources[type]=product&resources[limit]=5', { signal: searchAbort.signal })
            .then(function (response) {
              if (!response.ok) throw new Error('HTTP ' + response.status);
              return response.json();
            })
            .then(function (data) {
              predictiveSearch.setAttribute('aria-busy', 'false');
              var products = data.resources.results.products || [];

              while (searchResults.firstChild) {
                searchResults.removeChild(searchResults.firstChild);
              }

              if (products.length === 0) {
                var emptyDiv = document.createElement('div');
                emptyDiv.className = 'predictive-search__empty';
                emptyDiv.textContent = 'No products found';
                searchResults.appendChild(emptyDiv);
              } else {
                products.forEach(function (product) {
                  var link = document.createElement('a');
                  link.href = product.url;
                  link.className = 'predictive-search__item';

                  var imageWrapper = document.createElement('div');
                  imageWrapper.className = 'predictive-search__image';

                  var img = document.createElement('img');
                  img.src = product.image;
                  img.alt = product.title;
                  img.loading = 'lazy';
                  img.width = 60;
                  img.height = 60;
                  imageWrapper.appendChild(img);

                  var infoDiv = document.createElement('div');
                  infoDiv.className = 'predictive-search__info';

                  var titleSpan = document.createElement('span');
                  titleSpan.className = 'predictive-search__title';
                  titleSpan.textContent = product.title;

                  var priceSpan = document.createElement('span');
                  priceSpan.className = 'predictive-search__price';
                  priceSpan.textContent = product.price;

                  infoDiv.appendChild(titleSpan);
                  infoDiv.appendChild(priceSpan);

                  link.appendChild(imageWrapper);
                  link.appendChild(infoDiv);
                  searchResults.appendChild(link);
                });

                var viewAllLink = document.createElement('a');
                viewAllLink.href = searchUrl + '?q=' + encodeURIComponent(query);
                viewAllLink.className = 'predictive-search__view-all';
                viewAllLink.textContent = 'View all results for "' + query + '"';
                searchResults.appendChild(viewAllLink);
              }

              predictiveSearch.classList.add('is-visible');
              searchInput.setAttribute('aria-expanded', 'true');
            })
            .catch(function (error) {
              if (error.name === 'AbortError') return;
              predictiveSearch.setAttribute('aria-busy', 'false');
              while (searchResults.firstChild) searchResults.removeChild(searchResults.firstChild);
              var errDiv = document.createElement('div');
              errDiv.className = 'predictive-search__empty';
              errDiv.textContent = 'Something went wrong — press Enter to search';
              searchResults.appendChild(errDiv);
            });
        }, 300);
      });

      // Close on escape
      searchInput.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          predictiveSearch.classList.remove('is-visible');
          this.setAttribute('aria-expanded', 'false');
          this.blur();
        }
      });
    }

    // Announcement Bar - Close and Auto-hide
    var announcementBar = document.querySelector('[data-announcement-bar]');
    var announcementClose = document.querySelector('[data-announcement-close]');

    if (announcementBar) {
      // Close button functionality
      if (announcementClose) {
        announcementClose.addEventListener('click', function () {
          announcementBar.classList.add('is-hidden');
          // Store closed state in session
          sessionStorage.setItem('announcement_bar_closed', 'true');
        });
      }

      // Check if previously closed
      if (sessionStorage.getItem('announcement_bar_closed') === 'true') {
        announcementBar.classList.add('is-hidden');
      }

      // Announcement slider rotation. Clear any previous interval first so an
      // editor section reload doesn't leave the old timer running against
      // detached DOM (interval id held at module scope).
      var announcementSlides = announcementBar.querySelectorAll('.announcement-bar__slide');
      if (announcementIntervalId !== null) {
        clearInterval(announcementIntervalId);
        announcementIntervalId = null;
      }
      if (announcementSlides.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        var currentSlide = 0;
        announcementIntervalId = setInterval(function () {
          announcementSlides[currentSlide].classList.remove('is-active');
          currentSlide = (currentSlide + 1) % announcementSlides.length;
          announcementSlides[currentSlide].classList.add('is-active');
        }, 6000);
      }
    }

    // Document/window-level listeners are bound only once so editor section
    // reloads don't stack duplicate handlers. They read the live DOM at event
    // time (closure-captured nodes are replaced on shopify:section:load).
    if (!documentListenersBound) {
      documentListenersBound = true;

      // Escape key: close the mobile slide menu (and the predictive search if open)
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        var liveSlideMenu = document.querySelector('.header__slide-menu--open');
        if (liveSlideMenu && typeof activeCloseMenu === 'function') {
          activeCloseMenu();
        }
      });

      // Predictive search: close on click outside
      document.addEventListener('click', function (e) {
        var liveInput = document.querySelector('[data-search-input]');
        var liveResults = document.querySelector('[data-search-results]');
        var livePredictive = document.getElementById('predictive-search-results');
        if (!liveInput || !liveResults || !livePredictive) return;
        if (!liveInput.contains(e.target) && !liveResults.contains(e.target)) {
          livePredictive.classList.remove('is-visible');
          liveInput.setAttribute('aria-expanded', 'false');
        }
      });

      // Update wishlist count when storage changes (from other tabs/pages)
      window.addEventListener('storage', function (e) {
        if (e.key === 'deneb_wishlist') {
          updateWishlistCount();
        }
      });

      // Announcement bar: auto-hide on scroll
      var lastScroll = 0;
      var scrollThreshold = 100; // Hide after scrolling 100px

      window.addEventListener('scroll', function () {
        updateHeroHeaderState();

        var liveAnnouncementBar = document.querySelector('[data-announcement-bar]');
        if (!liveAnnouncementBar) return;
        var currentScroll = window.pageYOffset;

        // Auto-hide when scrolling down past threshold
        if (currentScroll > scrollThreshold && currentScroll > lastScroll) {
          liveAnnouncementBar.classList.add('is-hidden');
        }

        lastScroll = currentScroll;
      }, { passive: true });

      // Smart header hide/show on scroll (mobile only)
      var headerLastY = 0;
      var headerHidden = false;
      var HEADER_SCROLL_THRESHOLD = 10;

      window.addEventListener('scroll', function () {
        var header = document.querySelector('.header');
        if (!header) return;
        if (window.innerWidth > 768) return;
        var currentY = window.pageYOffset;
        var delta = currentY - headerLastY;

        // The homepage header is intentionally persistent: its transparent
        // state becomes solid over page content instead of sliding away.
        if (header.classList.contains('header--hero-overlay')) {
          header.classList.remove('header--hidden');
          headerHidden = false;
          headerLastY = currentY;
          return;
        }

        if (delta > HEADER_SCROLL_THRESHOLD && currentY > 60 && !headerHidden) {
          header.classList.add('header--hidden');
          headerHidden = true;
        } else if (delta < -HEADER_SCROLL_THRESHOLD && headerHidden) {
          header.classList.remove('header--hidden');
          headerHidden = false;
        }

        headerLastY = currentY;
      }, { passive: true });
    }
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHeader);
  } else {
    initHeader();
  }

  // Re-init against fresh DOM when the section is reloaded in the theme editor
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('[data-header-config]')) {
      if (document.body) document.body.dataset.headerJsInit = '';
      initHeader();
    }
  });
})();
